import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "../data/content";
import { EASE } from "./primitives";

export default function Header({ overHero = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  // Light text only when over a dark hero and not scrolled.
  const lightMode = overHero && !scrolled;
  const textColor = lightMode ? "text-ivory" : "text-charcoal";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ivory/90 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.05)] py-4"
            : "py-6 bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className={`font-serif text-xl md:text-2xl tracking-[0.15em] font-medium transition-colors duration-300 ${textColor}`}
          >
            CONNECT.CO
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`group relative font-sans text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${textColor}`}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${
                      active ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="header-book-call"
              className={`hidden md:inline-flex items-center gap-2 px-6 py-3 rounded-sm font-sans text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                lightMode
                  ? "bg-ivory text-charcoal hover:bg-gold hover:text-ivory"
                  : "bg-charcoal text-ivory hover:bg-gold"
              }`}
            >
              Book a Call
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>

            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="menu-open-btn"
              className={`lg:hidden transition-colors duration-300 ${textColor}`}
            >
              <Menu className="w-6 h-6" strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-night flex flex-col"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="font-serif text-xl tracking-[0.15em] text-ivory">CONNECT.CO</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="menu-close-btn"
                className="text-ivory"
              >
                <X className="w-7 h-7" strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="font-serif text-5xl text-ivory hover:text-gold transition-colors duration-300 py-2 block"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-12">
              <Link
                to="/contact"
                data-testid="mobile-book-call"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-ivory rounded-sm font-sans text-sm tracking-wide"
              >
                Book a Strategy Call <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
